import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  reload,
  updatePhoneNumber,
  verifyBeforeUpdateEmail,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { firebaseAuth, firebaseFunctions } from './firebase'

export type SyncContactIdentityResult = {
  ok: true
  email: string
  phone: string
  username: string
}

const syncContactIdentityCallable = httpsCallable<void, SyncContactIdentityResult>(
  firebaseFunctions,
  'syncAuthenticatedContactIdentity',
)

const currentUserOrThrow = () => {
  const user = firebaseAuth.currentUser
  if (!user) throw new Error('Sign in again before changing contact details.')
  return user
}

export const requestEmailChangeVerification = async (newEmail: string) => {
  const user = currentUserOrThrow()
  const trimmed = newEmail.trim().toLowerCase()
  if (!trimmed || !trimmed.includes('@')) throw new Error('Enter a valid email address.')
  if (trimmed === user.email?.toLowerCase()) throw new Error('This email is already on your account.')
  await verifyBeforeUpdateEmail(user, trimmed, {
    url: `${window.location.origin}/console/your`,
    handleCodeInApp: false,
  })
}

export const finishContactIdentitySync = async () => {
  const user = currentUserOrThrow()
  await reload(user)
  await user.getIdToken(true)
  const result = await syncContactIdentityCallable()
  return result.data
}

export const sendPhoneChangeCode = async (phoneNumber: string, recaptchaContainer: HTMLElement) => {
  const user = currentUserOrThrow()
  const trimmed = phoneNumber.trim()
  if (!trimmed.startsWith('+')) throw new Error('Use an international phone number, for example +919876543210.')
  if (trimmed === user.phoneNumber) throw new Error('This phone number is already on your account.')
  const verifier = new RecaptchaVerifier(firebaseAuth, recaptchaContainer, { size: 'invisible' })
  try {
    const provider = new PhoneAuthProvider(firebaseAuth)
    return await provider.verifyPhoneNumber(trimmed, verifier)
  } catch (error) {
    verifier.clear()
    throw error
  }
}

export const confirmPhoneChangeCode = async (verificationId: string, code: string) => {
  const user = currentUserOrThrow()
  const credential = PhoneAuthProvider.credential(verificationId, code.trim())
  await updatePhoneNumber(user, credential)
  await user.getIdToken(true)
  return finishContactIdentitySync()
}
